import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import extractText from 'flarum/common/utils/extractText';
import PersonRow from 'ext:ernestdefoe/parley/forum/components/PersonRow';
import ChatWindow from 'ext:ernestdefoe/parley/forum/components/ChatWindow';
import Calls from './Calls';
import Voice from './Voice';
import VoiceBar from './components/VoiceBar';
import CallLayer from './components/CallLayer';
import Icons from './icons';
import { callable } from './util';
const t = (key, params) => app.translator.trans('ernestdefoe-parley-calls.forum.' + key, params);
app.initializers.add('ernestdefoe-parley-calls', () => {
  /*
   * Started from `mount`, after Parley's own mount has created `app.parley`
   * (Parley loads first: this extension requires it).
   */
  extend(app, 'mount', function () {
    if (!app.parley || !app.session.user) return;
    const calls = app.parleyCalls = new Calls();
    if (!calls.available()) return;
    const voice = app.parleyVoice = new Voice();
    app.parley.callsInstalled = true;
    const root = document.createElement('div');
    root.id = 'ParleyCalls-root';
    document.body.appendChild(root);
    m.mount(root, CallLayer);

    // Bound straight to realtime's user channel. It fires at once if the
    // channel is already up and again after every reconnect, so no ring is
    // missed whichever of the two extensions finishes starting first.
    try {
      flarum.reg.get('flarum-realtime', 'forum/RealtimeState').onUserChannelReady(channel => {
        calls.bind(channel);
        voice.bind(channel);
      });
    } catch (e) {
      return;
    }
    calls.boot();
    voice.boot();
  });

  /*
   * 🚨 No settings-page entry point, deliberately.
   *
   * `import SettingsPage from 'flarum/forum/components/SettingsPage'` resolves
   * to undefined in this bundle — the registry holds the class directly, and
   * webpack's default-interop hands back nothing — so
   * `extend(SettingsPage.prototype, …)` threw "Cannot read properties of
   * undefined (reading 'prototype')". That killed the initializer at that
   * line, which took EVERY extender below it with it: the call buttons in the
   * online list and in the conversation header both silently stopped being
   * added. One button on the settings page is not worth that.
   *
   * The check is reached from the device-failure alerts, which is the moment
   * anybody wants it, and from `app.parleyCalls.checkDevices()` for anything
   * else that wants to place it.
   */

  // The hover buttons in the online list: voice and video replace "message"
  // for anyone who can be rung, exactly as the two editions differ in the mockup.
  extend(PersonRow.prototype, 'actionItems', function (items, p) {
    const calls = app.parleyCalls;
    if (!calls || !calls.available() || !callable(p)) return;
    items.remove('message');
    items.add('voice', button('phone', t('voice_call_with', {
      name: p.displayName
    }), () => callPerson(p, 'voice')), 90);
    items.add('video', button('video', t('video_call_with', {
      name: p.displayName
    }), () => callPerson(p, 'video')), 80);
  });

  // A room: its voice chat, as a strip under the header and a header button.
  extend(ChatWindow.prototype, 'bodyTopItems', function (items, conv) {
    if (!app.parleyVoice || conv.summary.type !== 'room') return;
    const room = {
      ...(conv.summary.room || {}),
      ...(app.parley.room(conv.summary.id) || {})
    };
    items.add('voice', m(VoiceBar, {
      room: room
    }));
  });
  extend(ChatWindow.prototype, 'headerItems', function (items, conv) {
    const v = app.parleyVoice;
    if (!v || conv.summary.type !== 'room') return;
    const room = {
      ...(conv.summary.room || {}),
      ...(app.parley.room(conv.summary.id) || {})
    };
    if (room.readonly && !app.parley.config.canModerate) return;
    const inHere = v.in(room.id);
    items.add('voice', m("button", {
      className: 'pl-ib' + (inHere ? ' pl-voice-on' : ''),
      "aria-pressed": String(inHere),
      title: extractText(t(inHere ? 'voice.leave' : 'voice.join')),
      "aria-label": extractText(t(inHere ? 'voice.leave' : 'voice.join')),
      onclick: () => inHere ? v.leave() : v.join(room)
    }, Icons.headphones(17)), 100);
  });

  // The window header, docked or on the messages page: voice and video in
  // front of search and the window's own buttons. The call itself is drawn
  // over the whole page by CallLayer, so it is the same wherever it starts.
  extend(ChatWindow.prototype, 'headerItems', function (items, conv) {
    const calls = app.parleyCalls;
    // A room reports isGroup false (it is not a group conversation), but it is
    // never a call: its own voice chat owns the 'voice' slot.
    if (!calls || !calls.available() || conv.summary.isGroup || conv.summary.type === 'room') return;
    const peer = conv.summary.participants[0];
    if (!peer) return;
    items.add('voice', button('phone', t('voice_call'), () => calls.start(conv.summary.id, 'voice', peer)), 100);
    items.add('video', button('video', t('video_call'), () => calls.start(conv.summary.id, 'video', peer)), 90);
  });
});
function button(icon, label, onclick) {
  const text = extractText(label);
  return m("button", {
    className: "pl-ib",
    title: text,
    "aria-label": text,
    onclick: onclick
  }, Icons[icon]());
}

/** A call needs a conversation to belong to; open (or find) it first. */
function callPerson(person, kind) {
  const s = app.parley;
  const existing = [...s.convs.values()].find(c => !c.summary.isGroup && c.summary.participants.some(x => x.id === person.id));
  const go = id => app.parleyCalls.start(id, kind, person);
  if (existing) return go(existing.summary.id);
  s.openWith(person.id).then(() => {
    const c = [...s.convs.values()].find(x => !x.summary.isGroup && x.summary.participants.some(y => y.id === person.id));
    if (c) go(c.summary.id);
  });
}